import { formatLedgerMoney } from '@/features/accounting';
import { Link } from 'react-router-dom';
import {
  forwardRef,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { vendorInvoiceAccountingPreviewSchema, type VendorCreditReview } from '@erp/shared';
import { apiFetch } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

type Review = NonNullable<VendorCreditReview['foreignFiscalReview']>;
export type VendorCreditForeignEditorHandle = {
  focusInvalid: (path: readonly (string | number)[]) => void;
};
type Props = {
  sourceInvoiceId: string;
  creditId: string;
  initial: Review | null;
  onChange: (value: Review | null, changed: boolean) => void;
};
export const VendorCreditForeignEditor = forwardRef<VendorCreditForeignEditorHandle, Props>(
  function VendorCreditForeignEditor({ sourceInvoiceId, creditId, initial, onChange }, ref) {
    const { t, i18n } = useTranslation();
    const prefix = useId();
    const evidenceInput = useRef<HTMLInputElement>(null);
    const mappingInputs = useRef(new Map<number, HTMLSelectElement>());
    const [evidence, setEvidence] = useState(initial?.evidenceText ?? '');
    const [mapping, setMapping] = useState<Review['sourcePositions']>(
      initial?.sourcePositions ?? [],
    );
    const source = useQuery({
      queryKey: ['foreign-credit-source', sourceInvoiceId],
      enabled: !!sourceInvoiceId,
      queryFn: () =>
        apiFetch(
          `/api/v1/procurement/vendor-invoices/${sourceInvoiceId}/accounting-preview`,
          vendorInvoiceAccountingPreviewSchema,
        ),
    });
    const credit = useQuery({
      queryKey: ['foreign-credit-positions', creditId],
      queryFn: () =>
        apiFetch(
          `/api/v1/procurement/vendor-invoices/${creditId}/accounting-preview`,
          vendorInvoiceAccountingPreviewSchema,
        ),
    });
    const context = source.data?.foreignCreditSource;
    useEffect(() => {
      if (!context || !credit.data) {
        onChange(null, false);
        return;
      }
      const positions = credit.data.splits.length
        ? credit.data.splits
        : credit.data.sourceBuckets.map((_, index) => ({ position: index + 1 }));
      const resolved = positions.map((row) => ({
        creditPosition: row.position,
        sourcePosition:
          mapping.find((part) => part.creditPosition === row.position)?.sourcePosition ??
          context.positions.find((part) => part.sourcePosition === row.position)?.sourcePosition ??
          0,
      }));
      onChange(
        {
          sourceFiscalReviewId: context.sourceFiscalReviewId,
          sourcePositions: resolved,
          evidenceText: evidence,
        },
        false,
      );
    }, [context, credit.data, mapping, evidence, onChange]);
    const positions = useMemo(
      () =>
        credit.data?.splits.length
          ? credit.data.splits
          : (credit.data?.sourceBuckets.map((_, index) => ({ position: index + 1 })) ?? []),
      [credit.data],
    );
    useImperativeHandle(
      ref,
      () => ({
        focusInvalid(path) {
          const index =
            path[0] === 'sourcePositions' && typeof path[1] === 'number' ? path[1] : null;
          const position = index === null ? undefined : positions[index]?.position;
          const target = position === undefined ? null : mappingInputs.current.get(position);
          (target ?? evidenceInput.current)?.focus();
        },
      }),
      [positions],
    );
    return (
      <fieldset className="space-y-3 rounded border p-3">
        <legend className="px-1 font-medium">{t('supplierCredits.foreignTitle')}</legend>
        <p className="text-sm text-muted-foreground">{t('supplierCredits.foreignExplain')}</p>
        {sourceInvoiceId ? (
          <Link
            className="text-sm underline"
            to={`/procurement/vendor-invoices/${sourceInvoiceId}/match`}
          >
            {t('supplierCredits.source')}
          </Link>
        ) : null}
        {source.isError || credit.isError ? (
          <div role="alert">
            <p>{t((source.error ?? credit.error)?.message ?? 'supplierCredits.sourceMissing')}</p>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                void source.refetch();
                void credit.refetch();
              }}
            >
              {t('supplierCredits.retry')}
            </Button>
          </div>
        ) : null}
        {source.isFetching || credit.isPending ? (
          <p role="status">{t('supplierCredits.loading')}</p>
        ) : null}
        {sourceInvoiceId && source.isSuccess && !context ? (
          <p role="alert">{t('supplierCredits.sourceMissing')}</p>
        ) : null}
        {context ? (
          <>
            <p className="text-sm">
              {t(
                context.sourceFiscalReviewId
                  ? 'supplierCredits.currentReview'
                  : 'supplierCredits.originalReview',
              )}
            </p>
            <p className="text-sm">
              {t('fiscalFx.reviewRateDate')}: {context.proof.rateTaxDate} ·{' '}
              {t('fiscalFx.reviewReportingDate')}: {context.proof.reportingTaxDate}
            </p>
            <p className="text-sm text-muted-foreground">{context.proof.review.evidenceText}</p>
            {context.proof.rows.map((row) => (
              <dl key={row.position} className="grid gap-2 border-t pt-2 text-sm sm:grid-cols-3">
                <div>
                  <dt>{t('supplierCredits.originalPosition')}</dt>
                  <dd>
                    {row.position} · {row.taxRate}%
                  </dd>
                </div>
                <div>
                  <dt>{t('fiscalFx.vendorLegal')}</dt>
                  <dd>{formatLedgerMoney(row.legalTax, 'EUR', i18n.language)}</dd>
                </div>
                <div>
                  <dt>{t('ledger.selfAssessment.deductible')}</dt>
                  <dd>{formatLedgerMoney(row.deductibleTax, 'EUR', i18n.language)}</dd>
                </div>
              </dl>
            ))}
            {positions.map((row) => (
              <div key={row.position} className="space-y-1">
                <Label htmlFor={`${prefix}-${row.position}`}>
                  {t('supplierCredits.creditPosition')} {row.position} ·{' '}
                  {t('supplierCredits.originalPosition')}
                </Label>
                <select
                  id={`${prefix}-${row.position}`}
                  ref={(element) => {
                    if (element) mappingInputs.current.set(row.position, element);
                    else mappingInputs.current.delete(row.position);
                  }}
                  className="min-h-11 w-full rounded border bg-background px-2"
                  value={
                    mapping.find((part) => part.creditPosition === row.position)?.sourcePosition ??
                    context.positions.find((part) => part.sourcePosition === row.position)
                      ?.sourcePosition ??
                    ''
                  }
                  onChange={(event) => {
                    setMapping((current) => [
                      ...current.filter((part) => part.creditPosition !== row.position),
                      { creditPosition: row.position, sourcePosition: Number(event.target.value) },
                    ]);
                    onChange(null, true);
                  }}
                >
                  <option value="">—</option>
                  {context.positions.map((part) => (
                    <option key={part.sourcePosition} value={part.sourcePosition}>
                      {part.sourcePosition} · {part.net} {source.data?.currency} · {part.rate}% ·{' '}
                      {part.inputTaxDeductionPercent}%
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </>
        ) : (
          <p className="text-sm">{t('supplierCredits.reviewPrerequisite')}</p>
        )}
        <Label htmlFor={`${prefix}-evidence`}>{t('supplierCredits.foreignEvidence')}</Label>
        <Input
          id={`${prefix}-evidence`}
          ref={evidenceInput}
          value={evidence}
          onChange={(event) => {
            setEvidence(event.target.value);
            onChange(null, true);
          }}
        />
      </fieldset>
    );
  },
);
